import getConfig from 'next/config';
import { useSelector } from 'react-redux';

import { trackClick } from '@/lib/tracking/tracking';
import { useObject } from '@/hooks/useObject';
import { usePreview } from '@/hooks/usePreview';

import { ProfileButton } from '../profileButton/ProfileButton';
import { ProfileCompany } from '../profileCompany/ProfileCompany';
import { ProfileTrust } from '../profileTrust/ProfileTrust';
import type { FhhState } from '@/domain';

const {
    publicRuntimeConfig: { baseUrl, contextPath },
} = getConfig();

export const Profile = () => {
    const objectData = useObject();
    const isPreview = usePreview();

    const profileIdentity = useSelector((state: FhhState) => state.profileData.identity);
    const { owner, adId, isAdmin, isImport, moreAdsQuery } = objectData;

    // Pulse tracker id: CE_ev20
    // TODO why use Common Elements (CE) and not our own definition?
    const trackHomepageClick = () =>
        trackClick({
            type: 'Click',
            intent: 'Open',
            name: 'Homepage link clicked',
            object: {
                id: 'homepage_link_clicked',
                type: 'UIElement',
                elementType: 'Link',
            },
            target: {
                id: owner.url,
                type: 'ExternalContent',
                name: 'Go to webpage',
                referralCommission: false,
                provider: owner.name,
            },
            vertical: {
                name: 'travel',
                subVertical: 'holiday_rental',
            },
        });

    // Pulse tracker id: FHH_ev02
    const trackMoreAdsClick = () =>
        trackClick({
            type: 'Click',
            intent: 'View',
            name: 'More ads from this advertiser',
            object: {
                id: 'more_ads_from_this_advertiser',
                type: 'UIElement',
                elementType: 'Link',
                hasExtendedProfile: false,
            },
            target: {
                id: `${baseUrl}/reise/feriehus-hytteutleie/resultat/`,
                type: 'Listing',
                name: 'Result page',
            },
            vertical: {
                name: 'travel',
                subVertical: 'holiday_rental',
            },
        });

    if (!owner) {
        return null;
    }

    return (
        <div data-testid="adProfile" className="text-center">
            {isAdmin || isImport ? <ProfileCompany objectData={objectData} /> : <ProfileTrust profileIdentity={profileIdentity} />}
            <ProfileButton objectData={objectData} isPreview={isPreview} />
            {!isImport && owner.url && (
                <a
                    href={`${contextPath}/ut?finnkode=${adId}&deepLinkParams=`}
                    data-testid="webpage"
                    className="block"
                    target="_blank"
                    rel="noopener noreferrer nofollow external"
                    onClick={() => trackHomepageClick()}>
                    Hjemmeside
                </a>
            )}
            {moreAdsQuery && (
                <a
                    data-testid="more-ads"
                    className="block"
                    href={`${contextPath}/resultat/?${moreAdsQuery}`}
                    onClick={() => trackMoreAdsClick()}>
                    Andre annonser fra samme utleier
                </a>
            )}
        </div>
    );
};
